import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, DeviceMobile, EnvelopeSimple, Phone, Siren, Ticket, WarningCircle, type Icon } from '@phosphor-icons/react';
import { Chip, ImmoButton, SegmentedControl, SlidePanel, WidgetGrid } from '../../../../components/immobilien/ui';
import { DL_GRUPPEN, DL_PANEL_KOPF, dlWidgets, istDlGruppe, istDlPanel, type DlGruppe, type DlPanel } from '../../../../components/immobilien/dienstleisterWidgets';
import { heuteIso, initialen } from '../../../../lib/immobilien/format';
import { avatarTon } from '../../../../lib/dienstleister/liste';
import { DL_STATUS, statusText } from '../../../../lib/dienstleister/status';
import { kicker, kiText, nachweisZeilen, vertragsInfo } from '../../../../lib/dienstleister/detail';
import { useDienstleisterDetail } from '../../../../lib/dienstleister/useDienstleisterDetail';
import { DlSeitenKontext, type DlSeitenWert } from './DienstleisterSeitenKontext';
import { DienstleisterWidget } from './DienstleisterWidget';
import { DienstleisterPanelInhalt } from './panels/DienstleisterPanelInhalt';

/** Schnellaktion im Kopf (Prototyp: 76×64, Radius 18, --fill2) – wie bei Mietern und Eigentümern. */
function Schnellaktion({ icon: IconKomp, label, href, onClick, deaktiviert, farbe = 'var(--acc)' }: {
  icon: Icon; label: string; href?: string; onClick?: () => void; deaktiviert?: string; farbe?: string;
}) {
  const stil: React.CSSProperties = {
    width: 'clamp(62px, 18vw, 76px)', height: 64, borderRadius: 18, gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ink)', background: 'var(--fill2)',
    border: 'none', textDecoration: 'none', opacity: deaktiviert ? 0.45 : 1,
  };
  const inhalt = <><IconKomp weight="fill" size={20} color={farbe} />{label}</>;
  if (href && !deaktiviert) return <a href={href} className="immo-hover-flaeche flex flex-col items-center justify-center" style={stil}>{inhalt}</a>;
  return (
    <button type="button" onClick={deaktiviert ? undefined : onClick} aria-disabled={deaktiviert ? true : undefined} title={deaktiviert}
      className={`flex flex-col items-center justify-center ${deaktiviert ? 'cursor-not-allowed' : 'immo-hover-flaeche cursor-pointer'}`} style={stil}>
      {inhalt}
    </button>
  );
}

const AVATAR: Record<ReturnType<typeof avatarTon>, { bg: string; fg: string }> = {
  grau: { bg: 'var(--fill2)', fg: 'var(--ink3)' }, bad: { bg: 'var(--bad-soft)', fg: 'var(--bad)' },
  acc: { bg: 'var(--acc-soft)', fg: 'var(--acc)' }, info: { bg: 'var(--info-soft)', fg: 'var(--info)' },
};
const tel = (s: string) => `tel:${s.replace(/[^\d+]/g, '')}`;
const txt = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/**
 * Dienstleister-Detail (Handoff Dienstleister §9, Screens 12, 13, 33, 35, 38): Kopf, Schnellaktionen,
 * Gruppenfilter und 19 Widgets; ein Klick öffnet ?panel=<key>.
 */
export default function DienstleisterSeite({ dienstleisterId }: { dienstleisterId: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { zustand, neuLaden } = useDienstleisterDetail(dienstleisterId);
  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const heute = heuteIso();

  const oeffnePanel = useCallback((key: DlPanel) => {
    setParams(p => { const n = new URLSearchParams(p); n.set('panel', key); return n; });
    window.scrollTo({ top: 0 });
  }, [setParams]);
  const verlassen = useRef<(() => void) | null>(null);
  const registriereVerlassen = useCallback((f: (() => void) | null) => { verlassen.current = f; }, []);
  const schliessePanel = useCallback(() => {
    verlassen.current = null;
    setParams(p => { const n = new URLSearchParams(p); n.delete('panel'); return n; });
  }, [setParams]);
  const zurueck = useCallback(() => { if (verlassen.current) verlassen.current(); else schliessePanel(); }, [schliessePanel]);
  const setzeGruppe = (g: DlGruppe) => setParams(p => { const n = new URLSearchParams(p); if (g === 'alle') n.delete('grp'); else n.set('grp', g); return n; });
  const zumObjekt = useCallback((id: string) => navigate(`/admin/objekte/${encodeURIComponent(id)}`), [navigate]);

  const wert: DlSeitenWert | null = useMemo(() => {
    if (!daten || zustand.status !== 'ok') return null;
    const d = daten.dienstleister;
    const z = daten.zeile;
    const nachweise = nachweisZeilen(daten.nachweise, z.handwerk, heute);
    const regeln = daten.regeln.filter(r => r.gewerk_id && daten.kategorieIds.includes(r.gewerk_id));
    return {
      daten, fehler: zustand.fehler, name: z.name, email: z.email, telefon: z.telefon, notdienst: txt(d.notdienst_telefon), heute,
      vertretung: d.vertretung === true, nachweise, vertrag: vertragsInfo(daten.zuordnungen, heute), regeln,
      ki: kiText({
        name: z.name, ansprechpartner: z.ansprechpartner, partnerSeit: txt(d.partner_seit) || null, heute, kategorien: z.kategorien, objekte: z.objektNamen,
        bewertung: z.bewertung, offeneAuftraege: z.offeneAuftraege, offen: z.offen, nachweise,
      }),
      oeffnePanel, neuLaden, registriereVerlassen, schliessePanel, zumObjekt,
    };
  }, [daten, zustand, heute, oeffnePanel, neuLaden, registriereVerlassen, schliessePanel, zumObjekt]);

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Dienstleister wird geladen" className="flex flex-col" style={{ gap: 16 }}>
        <div className="animate-pulse" style={{ height: 128, borderRadius: 30, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 40, width: 520, maxWidth: '100%', borderRadius: 16, background: 'var(--fill2)' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gridAutoRows: 150, gap: 14 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      </div>
    );
  }
  if (zustand.status === 'fehlt' || zustand.status === 'fehler' || !wert) {
    return (
      <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
        <WarningCircle weight="fill" size={36} color="var(--bad)" />
        <span style={{ fontSize: 18, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Dienstleister nicht gefunden' : 'Dienstleister konnte nicht geladen werden'}</span>
        {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{zustand.meldung}</span>}
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={() => navigate('/admin/dienstleister')}>Alle Dienstleister</ImmoButton>
          {zustand.status === 'fehler' && <ImmoButton art="akzent" icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>}
        </div>
      </div>
    );
  }

  const z = wert.daten.zeile;
  const d = wert.daten.dienstleister;
  const st = DL_STATUS[z.status];
  const av = AVATAR[avatarTon(z)];
  const rohGruppe = params.get('grp');
  const gruppe: DlGruppe = istDlGruppe(rohGruppe) ? rohGruppe : 'alle';
  const rohPanel = params.get('panel');
  const panel = istDlPanel(rohPanel) ? rohPanel : null;
  const kopf = panel ? DL_PANEL_KOPF[panel] : null;
  const fehlerBereiche = Object.keys(wert.fehler);
  const chipText = [z.kategorien.join(', ') || 'Keine Kategorie', z.objektIds.length === 1 ? '1 Objekt' : `${z.objektIds.length} Objekte`, z.ansprechpartner].filter(Boolean).join(' · ');

  return (
    <DlSeitenKontext.Provider value={wert}>
      <div className="flex flex-col" style={{ gap: 16 }}>
        <section className="immo-glas immo-side-in relative flex flex-wrap items-center overflow-hidden" style={{ borderRadius: 30, padding: 22, gap: 18 }} aria-label="Dienstleister">
          <span className="flex items-center justify-center shrink-0" style={{ width: 72, height: 72, borderRadius: '50%', fontSize: 24, fontWeight: 800, background: av.bg, color: av.fg }}>
            {initialen(wert.name)}
          </span>
          <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              {kicker(z, wert.vertretung, txt(d.partner_seit) || null)}
            </span>
            <h1 className="m-0" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{wert.name}</h1>
            <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
              <button type="button" onClick={() => oeffnePanel(z.objektIds.length ? 'objekte' : 'kategorien')} className="immo-hover-flaeche flex items-center cursor-pointer min-w-0"
                style={{ height: 26, maxWidth: '100%', padding: '0 10px', borderRadius: 13, gap: 5, border: 'none', fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
                <span className="truncate">{chipText}</span>
              </button>
              <Chip ton={st.ton} groesse="s" punkt>{statusText(z.status, z.vertragEndet)}</Chip>
              <Chip ton="neutral" groesse="s" icon={DeviceMobile}>Kein Portal</Chip>
            </div>
          </div>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <Schnellaktion icon={Phone} label="Anrufen" href={wert.telefon ? tel(wert.telefon) : undefined} deaktiviert={wert.telefon ? undefined : 'Keine Telefonnummer hinterlegt'} />
            {wert.notdienst && <Schnellaktion icon={Siren} label="Notdienst" farbe="var(--bad)" href={tel(wert.notdienst)} />}
            <Schnellaktion icon={EnvelopeSimple} label="E-Mail" href={wert.email ? `mailto:${wert.email}` : undefined} deaktiviert={wert.email ? undefined : 'Keine E-Mail hinterlegt'} />
            <Schnellaktion icon={Ticket} label="Auftrag" onClick={() => oeffnePanel('tickets')} />
          </div>
        </section>

        {fehlerBereiche.length > 0 && (
          <div role="status" className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
            <WarningCircle weight="fill" size={18} />
            Einige Bereiche konnten nicht geladen werden ({fehlerBereiche.join(', ')}).
            <button type="button" onClick={neuLaden} className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, textDecoration: 'underline' }}>Erneut laden</button>
          </div>
        )}

        {kopf && panel ? (
          <SlidePanel key={panel} titel={kopf.titel} icon={kopf.icon} onZurueck={zurueck}>
            <DienstleisterPanelInhalt panel={panel} />
          </SlidePanel>
        ) : (
          <>
            <SegmentedControl art="akzent" ariaLabel="Widget-Gruppen" wert={gruppe} onChange={setzeGruppe} className="self-start" segmente={DL_GRUPPEN.map(g => ({ key: g.key, label: g.label }))} />
            <WidgetGrid minSpalte={160} zeile={150} abstand={14}
              elemente={dlWidgets(gruppe).map(def => ({ key: def.key, groesse: def.groesse, inhalt: <DienstleisterWidget def={def} s={wert} onOeffnen={() => oeffnePanel(def.key)} /> }))} />
          </>
        )}
      </div>
    </DlSeitenKontext.Provider>
  );
}
